// App.jsx (or App.js)
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";

const PROMPT = "matt@site:~$ ";
const COMMAND = "cat ./intel/profile/matt_teal.txt";

export default function App() {
  const bootLines = useMemo(
    () => [
      "###############################",
      "Initializing secure shell…",
      "Ready",
      "###############################",
      "",
    ],
    []
  );

  const dossier = useMemo(
    () => [
      { kind: "text", value: "Matt Teal" },
      { kind: "text", value: "" },
      { kind: "text", value: "Occupation: software engineer" },
      { kind: "text", value: "" },
      { kind: "text", value: "Years of Experience: 5" },
      { kind: "text", value: "" },
      { kind: "text", value: "Known Associates:" },
      { kind: "text", value: "  JPMorgan Chase" },
      { kind: "text", value: "  Publicis Sapient" },
      { kind: "text", value: "  NASA" },
      { kind: "text", value: "" },

      { kind: "text", value: "Recent Work (high signal):" },
    { kind: "text", value: "  • Built internal AI-powered data lineage platform with React, FastAPI, Neo4j knowledge graphs, and Neo4j GraphRAG" },
    { kind: "text", value: "  • Enabled end-to-end dependency analysis across legacy Oracle ecosystems; onboarded 2 enterprise apps with architecture designed to scale to 40+" },
    { kind: "text", value: "  • Optimized cloud migration workflow by simplifying proposed 3-stage Airflow orchestration into a 2-stage execution model" },
    { kind: "text", value: "  • Enhanced event-driven Airflow-on-EKS platform using EventBridge, Lambda, S3, and Databricks to process thousands of feeds and 100+ GB daily" },
    { kind: "text", value: "  • Built self-service Airflow onboarding workflows and documentation so engineers can independently onboard new data feeds" },
    { kind: "text", value: "  • Developed Airflow operational analytics tooling with runtime metrics, success/failure reporting, drill-downs, and LLM-powered natural-language querying" },
    { kind: "text", value: "" },
    
    { kind: "text", value: "Abilities:" },
    { kind: "text", value: "┌────────────────────┬───────────────────────────────┐" },
    { kind: "text", value: "│ Category           │ Tools / Technologies          │" },
    { kind: "text", value: "├────────────────────┼───────────────────────────────┤" },
    { kind: "text", value: "│ Languages          │ Python, Java, JavaScript      │" },
    { kind: "text", value: "│                    │ SQL, Spark SQL, Go            │" },
    { kind: "text", value: "├────────────────────┼───────────────────────────────┤" },
    { kind: "text", value: "│ Frontend           │ React.js, Vue.js, Bootstrap   │" },
    { kind: "text", value: "├────────────────────┼───────────────────────────────┤" },
    { kind: "text", value: "│ Backend / APIs     │ FastAPI, GraphQL, Express.js  │" },
    { kind: "text", value: "├────────────────────┼───────────────────────────────┤" },
    { kind: "text", value: "│ Data / AI          │ Neo4j, GraphRAG, Databricks   │" },
    { kind: "text", value: "│                    │ Snowflake, LLM Tooling        │" },
    { kind: "text", value: "├────────────────────┼───────────────────────────────┤" },
    { kind: "text", value: "│ Cloud / Platform   │ AWS S3, Lambda, EventBridge   │" },
    { kind: "text", value: "│                    │ SQS, EC2, EKS, Kubernetes     │" },
    { kind: "text", value: "├────────────────────┼───────────────────────────────┤" },
    { kind: "text", value: "│ DevOps / Infra     │ Terraform, Docker, CI/CD      │" },
    { kind: "text", value: "│                    │ OpenTelemetry, Datadog        │" },
    { kind: "text", value: "└────────────────────┴───────────────────────────────┘" },
    { kind: "text", value: "" },

      { kind: "text", value: "Last Seen: unknown" },
      { kind: "text", value: "" },

      { kind: "text", value: "Last Known Contacts:" },
      {
        kind: "link",
        prefix: "  LinkedIn: ",
        text: "linkedin.com/in/matt-teal",
        href: "https://www.linkedin.com/in/matt-teal/",
      },
      {
        kind: "link",
        prefix: "  Email: ",
        text: "matt.tealcosc@gmail.com",
        href: "mailto:matt.tealcosc@gmail.com",
      },
      { kind: "text", value: "" },

      { kind: "text", value: "Tracking Priority: critical" },
      { kind: "text", value: "" },
    ],
    []
  );

  const [bootShown, setBootShown] = useState([]);
  const [typedCmd, setTypedCmd] = useState("");
  const [shownDossier, setShownDossier] = useState([]);
  const [phase, setPhase] = useState("boot"); // boot | typingCmd | printing | done

  // Interactive shell state (after done)
  const [history, setHistory] = useState([]); // array of strings for entered commands
  const [input, setInput] = useState("");

  const terminalRef = useRef(null);
  const inputRef = useRef(null);

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const focusInput = () => {
    // focus the hidden input so keystrokes go to the shell
    inputRef.current?.focus();
  };

  useEffect(() => {
    // BOOT
    if (prefersReducedMotion) {
      setBootShown(bootLines);
      setPhase("typingCmd");
      return;
    }

    let i = 0;
    setBootShown([]);
    setPhase("boot");

    const t = setInterval(() => {
      i += 1;
      setBootShown(bootLines.slice(0, i));
      if (i >= bootLines.length) {
        clearInterval(t);
        setTimeout(() => setPhase("typingCmd"), 200);
      }
    }, 120); // slower boot

    return () => clearInterval(t);
  }, [bootLines, prefersReducedMotion]);

  useEffect(() => {
    // TYPE COMMAND
    if (phase !== "typingCmd") return;

    if (prefersReducedMotion) {
      setTypedCmd(COMMAND);
      setPhase("printing");
      return;
    }

    let i = 0;
    setTypedCmd("");

    const t = setInterval(() => {
      i += 1;
      setTypedCmd(COMMAND.slice(0, i));
      if (i >= COMMAND.length) {
        clearInterval(t);
        setTimeout(() => setPhase("printing"), 350);
      }
    }, 65); // slower typing

    return () => clearInterval(t);
  }, [phase, prefersReducedMotion]);

  useEffect(() => {
    // PRINT DOSSIER
    if (phase !== "printing") return;

    if (prefersReducedMotion) {
      setShownDossier(dossier);
      setPhase("done");
      return;
    }

    let i = 0;
    setShownDossier([]);

    const t = setInterval(() => {
      i += 1;
      setShownDossier(dossier.slice(0, i));
      if (i >= dossier.length) {
        clearInterval(t);
        setPhase("done");
      }
    }, 30); // slower output

    return () => clearInterval(t);
  }, [phase, dossier, prefersReducedMotion]);

  useEffect(() => {
    // When terminal is ready, focus input
    if (phase === "done") {
      // small delay so it feels natural
      setTimeout(() => focusInput(), 100);
    }
  }, [phase]);

  useEffect(() => {
    // keep terminal scrolled to bottom as history grows
    if (!terminalRef.current) return;
    terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
  }, [bootShown, typedCmd, shownDossier, history, phase]);

  const onKeyDown = (e) => {
    if (phase !== "done") return;

    if (e.key === "Enter") {
      e.preventDefault();
      const cmd = input; // keep even empty commands
      setHistory((h) => [...h, cmd]);
      setInput("");
      return;
    }

    // Optional: basic ctrl+l clear (classic terminal)
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "l") {
      e.preventDefault();
      setHistory([]);
      return;
    }
  };

  return (
    <main className="terminal" aria-label="Terminal" onClick={focusInput}>
      <div className="terminal-content" ref={terminalRef}>
        {/* boot */}
        {bootShown.length > 0 && (
          <div className="boot">
            {bootShown.map((l, idx) => (
              <div className="out-line" key={idx}>
                {l === "" ? "\u00A0" : l}
              </div>
            ))}
          </div>
        )}

        {/* auto command line */}
        <div className="line">
          <span className="prompt">{PROMPT}</span>
          <span className="cmd">{typedCmd}</span>
          {phase === "typingCmd" && <span className="cursor" aria-hidden="true" />}
        </div>

        {/* dossier output */}
        {shownDossier.length > 0 && (
          <div className="output" role="region" aria-label="Dossier output">
            {shownDossier.map((item, idx) => {
              if (item.kind === "link") {
                return (
                  <div className="out-line" key={idx}>
                    <span className="out-text">{item.prefix}</span>
                    <a className="link" href={item.href} target="_blank" rel="noreferrer">
                      {item.text}
                    </a>
                  </div>
                );
              }
              return (
                <div className="out-line" key={idx}>
                  {item.value === "" ? "\u00A0" : item.value}
                </div>
              );
            })}
          </div>
        )}

        {/* interactive history (no output, just echo command) */}
        {phase === "done" && history.length > 0 && (
          <div className="shell-history">
            {history.map((cmd, idx) => (
              <div className="line" key={idx}>
                <span className="prompt">{PROMPT}</span>
                <span className="cmd">{cmd}</span>
              </div>
            ))}
          </div>
        )}

        {/* interactive prompt */}
        {phase === "done" && (
          <div className="line">
            <span className="prompt">{PROMPT}</span>
            <span className="cmd">{input}</span>
            <span className="cursor" aria-hidden="true" />

            {/* hidden input captures keystrokes */}
            <input
              ref={inputRef}
              className="shell-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
        )}
      </div>
    </main>
  );
}
